const IMAGE_LISTING_NOTE =
  "Image-derived listing: the title and description use only features visible in the two supplied product images. Brand, manufacturer, exact dimensions, capacity and material composition are not assumed when they are not shown.";

const makeProduct = (item) => {
  const key = String(item.id).padStart(2, "0");
  const images = [
    `/assets/products/product-${key}-1.jpg`,
    `/assets/products/product-${key}-2.jpg`,
  ];
  return {
    brand: "Not shown in supplied images",
    manufacturer: "Not shown in supplied images",
    image: images[0],
    images,
    colors: [],
    size: ["One item (as pictured)"],
    purchasable: true,
    listingNote: IMAGE_LISTING_NOTE,
    ...item,
    price: Number(item.price).toFixed(2),
    description: [item.use, item.detail],
    Features: item.features,
  };
};

const productData = [
  makeProduct({
    id: 1,
    name: "Unbranded Silver Threaded Gas Cylinder Pair",
    category: "Pressurised Containers",
    price: 168,
    inventory: 0,
    purchasable: false,
    size: ["Pair (as pictured)"],
    colors: ["Brushed silver / brass tone / white"],
    material: "Visible only: silver-colour metal cylinders, brass-tone threaded valve assemblies and blank white label areas. The alloy, liner and valve materials are not identified.",
    specifications: "Two tall cylinders with threaded top valves are pictured. No maker, gas type, valve standard, rated pressure, compatible appliance, inspection date or fill status is visible.",
    use: "The images resemble a pair of refillable gas cylinders, but the intended gas and appliance compatibility cannot be established from appearance alone.",
    detail: "The photographs do not prove that the cylinders contain CO2, are filled, are food-service rated or are safe to connect.",
    care: "Do not fill, transport, connect or use these cylinders until the physical label, inspection record and supplier documentation have been verified.",
    cautions: "PRESSURISED-CONTAINER HOLD: missing identification can create rupture, leakage, incompatibility and transport risks.",
    features: ["Two cylinders pictured", "Threaded top valves", "Blank label areas"],
    holdReason: "Unavailable until the supplier provides the manufacturer, gas type, compatible models, valve standard, rated pressure, inspection date, fill state and required transport/safety documents.",
  }),
  makeProduct({
    id: 2,
    name: "Unbranded Charcoal Push-Up Balm Stick",
    category: "Personal Care — Documentation Pending",
    price: 98,
    inventory: 0,
    purchasable: false,
    colors: ["Charcoal casing / off-white balm"],
    material: "Visible only: a dark cylindrical push-up casing containing a pale solid balm. The casing composition and cosmetic ingredients are not shown.",
    specifications: "One unlabeled balm stick is pictured with orchid styling props. No net weight, ingredient panel, batch code, period-after-opening mark or manufacturer is visible.",
    use: "The push-up format suggests a solid personal-care balm, but the intended body area and product function cannot be confirmed from the images.",
    detail: "The orchid prop describes the photograph only; it is not evidence of fragrance, extract, formula or product name.",
    care: "Do not apply or distribute until the sealed product label and cosmetic safety documentation are available.",
    cautions: "COSMETIC HOLD: unknown ingredients and allergens prevent an informed patch test or safe-use assessment.",
    features: ["Push-up format", "Off-white solid balm", "Unlabeled charcoal casing"],
    holdReason: "Unavailable until the supplier provides a full ingredient list, allergens/fragrance disclosure, net weight, batch and expiry/PAO details, responsible manufacturer and safe-use directions.",
  }),
  makeProduct({
    id: 3,
    name: "Rose-Lid Glass Cup with Cork-Style Sleeve",
    category: "Drinkware",
    price: 158,
    inventory: 19,
    colors: ["Clear / cork brown / dusty rose"],
    material: "Visible only: a clear rigid cup, cork-textured grip sleeve and rose-colour flexible lid. Exact glass and lid grades are not shown.",
    specifications: "One tapered cup with a removable sleeve and sip opening is pictured. Capacity, heat rating and leak resistance are not visible.",
    use: "Shown holding a coffee-colour drink as a reusable takeaway-style cup.",
    detail: "The second image shows the lid removed and the sleeve fitted around the middle of the cup.",
    care: "Separate the visible parts for gentle washing and dry the sleeve fully; follow the physical label for temperature and dishwasher limits.",
    cautions: "Do not assume the lid is leakproof or the cup is safe for boiling liquid. Stop use if the rigid vessel chips or cracks.",
    features: ["Removable rose lid", "Cork-textured sleeve", "Clear tapered cup"],
  }),
  makeProduct({
    id: 4,
    name: "Long-Handle Wood-Finish Body Brush",
    category: "Bath Accessories",
    price: 118,
    inventory: 23,
    colors: ["Natural wood tone / cream"],
    material: "Visible only: a wood-grain handle, pale dense bristles and a light hanging cord. The wood species and bristle fibre are not identified.",
    specifications: "One long curved handle with an oval bristle head and hanging hole is pictured.",
    use: "The long handle and bristle head indicate a bath or dry-body brush intended to reach the back and legs.",
    detail: "The close view shows two bristle lengths, with a denser textured centre and longer outer rows.",
    care: "Use light pressure, rinse after wet use and hang with airflow. Do not soak the handle.",
    cautions: "Avoid the face, broken skin and irritated areas. Fibre firmness and allergy information are not supplied in the images.",
    features: ["Long curved handle", "Oval bristle head", "Hanging cord"],
  }),
  makeProduct({
    id: 5,
    name: "Lavender-Tone Cosmetic Gel Jar with Spatula",
    category: "Cosmetics — Documentation Pending",
    price: 138,
    inventory: 0,
    purchasable: false,
    colors: ["Translucent lavender / sage lid / clear spatula"],
    material: "Visible only: a translucent purple gel in a clear jar with a sage-colour lid and clear spatula. Formula and packaging materials are not identified.",
    specifications: "One unlabeled cosmetic-style jar and spatula are pictured. No volume, ingredient list, batch code, expiry/PAO mark or manufacturer is visible.",
    use: "The jar and applicator suggest a topical cosmetic gel, but its purpose, application area and contact time cannot be confirmed from appearance.",
    detail: "Lavender flowers are styling props and do not prove that lavender, vitamin E or any other ingredient is present.",
    care: "Keep sealed and do not apply until the complete physical label and safe-use directions have been checked.",
    cautions: "COSMETIC HOLD: do not infer benefits, ingredients or skin suitability from colour or props.",
    features: ["Translucent gel appearance", "Sage-colour lid", "Spatula pictured"],
    holdReason: "Unavailable until ingredients, allergens, intended use, net contents, batch, expiry/PAO, manufacturer and directions are documented.",
  }),
  makeProduct({
    id: 6,
    name: "Polished Brown Shell-Style Bowl",
    category: "Bowls",
    price: 78,
    inventory: 16,
    colors: ["Dark brown / natural mottling"],
    material: "Visible only: a thin, polished, brown bowl with irregular pale grain-like markings. The source material and food-contact finish are not shown.",
    specifications: "One rounded bowl with a naturally irregular-looking surface is pictured. Diameter, depth and capacity are not provided.",
    use: "A lightweight-looking bowl suitable for display or dry-item storage when used within the instructions supplied with the physical product.",
    detail: "The photographs do not establish that the bowl is coconut shell, handmade or food safe.",
    care: "Wipe or hand-wash gently only if the physical care label permits it; dry promptly and avoid heat.",
    cautions: "Food contact, microwave, oven and dishwasher suitability are not verified by the images.",
    features: ["Rounded profile", "Polished dark surface", "Irregular mottled pattern"],
  }),
  makeProduct({
    id: 7,
    name: "Blue & White Bead Refill Pouches",
    category: "Household Refill — Documentation Pending",
    price: 68,
    inventory: 0,
    purchasable: false,
    size: ["Two pouches (as pictured)"],
    colors: ["Kraft brown / white / blue"],
    material: "Visible only: two unlabelled kraft resealable pouches containing small white and blue beads. Chemical composition and pouch materials are not identified.",
    specifications: "Two differently sized pouches and loose beads are shown. Weight, purpose, dosage and compatible appliance or container are not visible.",
    use: "The images show a granular refill format, but they do not establish that the contents are laundry minerals, fragrance beads or another product.",
    detail: "No scent, wash count, cleaning claim or environmental claim can be supported from the photographs.",
    care: "Keep sealed and away from children, pets, food and moisture until the contents and safe handling instructions are confirmed.",
    cautions: "CHEMICAL-CONTENTS HOLD: do not ingest, mix, heat or use unidentified beads.",
    features: ["Two resealable pouches", "Blue and white beads", "Unlabelled packaging"],
    holdReason: "Unavailable until the supplier provides product identity, composition, intended use, dosage, hazard/first-aid information, net weight, batch and manufacturer.",
  }),
  makeProduct({
    id: 8,
    name: "Blue Woven Relaxation Pillow with Removable Insert",
    category: "Relaxation Accessories — Documentation Pending",
    price: 108,
    inventory: 0,
    purchasable: false,
    colors: ["Blue-grey cover / natural insert"],
    material: "Visible only: a blue-grey woven outer cover and a natural-colour fabric insert. Fibre and filling composition are not shown.",
    specifications: "One rectangular pillow, one removable insert and a plain kraft box are pictured. Exact dimensions, fill weight and closure details are not supplied.",
    use: "The narrow rectangular form suggests an eye or relaxation pillow, but weight, aroma and hot/cold suitability cannot be confirmed from appearance.",
    detail: "Lavender flowers and loose buds are styling props; they do not prove that the insert contains lavender or grain.",
    care: "Do not heat, chill or wash the filled insert without verified instructions. Keep the insert dry.",
    cautions: "FILLING-CONTENTS HOLD: unknown botanical or grain fillings may create allergy, moisture and overheating risks.",
    features: ["Rectangular woven cover", "Removable inner insert", "Plain kraft box pictured"],
    holdReason: "Unavailable until filling, allergens, dimensions, weight, manufacturer, batch and verified heating/cooling and cleaning instructions are supplied.",
  }),
  makeProduct({
    id: 9,
    name: "Mustard & Sage Porous Sponge Pair",
    category: "Cleaning Accessories",
    price: 58,
    inventory: 42,
    size: ["Pair (as pictured)"],
    colors: ["Mustard / sage"],
    material: "Visible only: two rectangular porous foam-like sponges. Fibre or polymer composition is not shown.",
    specifications: "Two similarly sized sponges with open-cell texture are pictured, dry and beside water.",
    use: "A two-colour sponge pair for routine wiping or washing on surfaces compatible with the physical product instructions.",
    detail: "The photographs do not prove cellulose content, compostability or expansion performance.",
    care: "Rinse after use, squeeze gently and air-dry. Replace if odour, tearing or embedded debris develops.",
    cautions: "Test on a hidden area before using on delicate finishes; keep away from hot cookware and flame.",
    features: ["Two-piece set", "Open porous texture", "Two identifying colours"],
  }),
  makeProduct({
    id: 10,
    name: "Unbranded Sage Push-Up Balm Stick",
    category: "Personal Care — Documentation Pending",
    price: 98,
    inventory: 0,
    purchasable: false,
    colors: ["Sage casing / off-white balm"],
    material: "Visible only: a sage-colour cylindrical push-up casing containing a pale solid balm. Ingredients and casing composition are not shown.",
    specifications: "One unlabeled balm stick is pictured with green citrus styling props. Net weight, formula, batch and manufacturer are not visible.",
    use: "The format suggests a solid personal-care balm, but deodorant use and body-area suitability cannot be confirmed from the images.",
    detail: "Citrus props do not prove lime, bergamot, fragrance or any ingredient claim.",
    care: "Do not apply or distribute until the sealed product label and cosmetic safety documentation are available.",
    cautions: "COSMETIC HOLD: unknown ingredients and allergens prevent an informed safe-use assessment.",
    features: ["Push-up format", "Off-white solid balm", "Unlabeled sage casing"],
    holdReason: "Unavailable until a complete ingredient and allergen list, net weight, batch, expiry/PAO, responsible manufacturer and directions are documented.",
  }),
  makeProduct({
    id: 11,
    name: "Natural-Tone Mesh Soap Pouch",
    category: "Bath Accessories",
    price: 48,
    inventory: 47,
    colors: ["Natural cream"],
    material: "Visible only: a coarse woven natural-tone pouch, braided drawcord, wooden bead and tassel. Fibre species is not shown.",
    specifications: "One drawstring pouch is pictured holding a rectangular soap bar; dimensions are not provided.",
    use: "A mesh pouch for holding and lathering a compatible soap bar while the cord provides a hanging point.",
    detail: "The open weave is visible from both sides; exfoliation level and fibre identity are not certified by the image.",
    care: "Rinse after use, empty trapped residue and hang fully open to dry.",
    cautions: "Use gently and avoid broken or irritated skin. Replace if the cord, bead or weave becomes damaged.",
    features: ["Open woven pouch", "Drawstring closure", "Wood-look bead and tassel"],
  }),
  makeProduct({
    id: 12,
    name: "Coral Flexible Straw & Cleaning Kit",
    category: "Drink Accessories",
    price: 62,
    inventory: 54,
    colors: ["Coral / silver tone"],
    material: "Visible only: a coral flexible tube, a slim metal-wire brush and a silver-colour screw-top case. Material and food-contact grades are not shown.",
    specifications: "One coilable straw-like tube, one cleaning brush and one cylindrical storage case are pictured.",
    use: "A portable drinking-straw-style kit with a brush for cleaning the inside of the tube.",
    detail: "The second view shows all three pieces separated and the case open.",
    care: "Clean through the full bore and dry every piece before closing the case.",
    cautions: "Verify food-contact and temperature ratings on the physical packaging before use; keep the small brush away from children.",
    features: ["Flexible coral tube", "Slim cleaning brush", "Screw-top travel case"],
  }),
  makeProduct({
    id: 13,
    name: "Sage-Sleeve Glass Travel Cup",
    category: "Drinkware",
    price: 148,
    inventory: 17,
    colors: ["Clear / sage"],
    material: "Visible only: a clear rigid cup with a sage-colour sleeve and matching sip lid. Exact glass and flexible-material grades are not shown.",
    specifications: "One tall tapered cup with removable lid and grip sleeve is pictured. Capacity, heat rating and leak resistance are not provided.",
    use: "Shown holding a coffee-colour drink as a reusable takeaway-style cup.",
    detail: "The second image shows the wide rim and removable lid beside the cup.",
    care: "Separate all visible parts for washing and dry before reassembly; follow the physical label for heat and dishwasher limits.",
    cautions: "Do not assume the lid is leakproof or the cup tolerates boiling liquid. Stop use if the rigid vessel chips or cracks.",
    features: ["Removable sip lid", "Full grip sleeve", "Clear tapered cup"],
  }),
  makeProduct({
    id: 14,
    name: "Charcoal & Rust Heart-Motif Ribbed Socks",
    category: "Socks",
    price: 72,
    inventory: 38,
    size: ["One pair; size not shown"],
    colors: ["Charcoal / rust"],
    material: "Visible only: a ribbed dark knit with rust-colour cuffs, heels, toes and a small heart motif. Fibre composition is not shown.",
    specifications: "One mid-calf-style pair is pictured. Size, yarn blend and reinforcement details are not visible.",
    use: "A casual two-tone pair of ribbed socks for everyday wear when the physical size label is suitable.",
    detail: "The small rust heart appears near the outer ankle of one sock; no wording or recipient theme is shown.",
    care: "Wash according to the sewn-in label with similar colours and reshape to air-dry.",
    cautions: "Confirm size and fibre sensitivities from the physical label before wear.",
    features: ["Ribbed knit", "Contrast cuff, heel and toe", "Small heart motif"],
  }),
  makeProduct({
    id: 15,
    name: "Sage Mixed-Page Hardcover Notebook",
    category: "Stationery",
    price: 168,
    inventory: 25,
    colors: ["Muted sage / cream pages"],
    material: "Visible only: a textured sage hardcover, cream paper, elastic closure and ribbon marker. Cover and paper composition are not shown.",
    specifications: "One rounded-corner notebook is pictured with at least one ruled page and one dotted page. Page count and dimensions are not visible.",
    use: "A flexible notebook for writing, planning, lists or dot-grid layouts.",
    detail: "The images do not show dated prompts, a 12-week programme or wellness content.",
    care: "Keep dry and test ink on an inconspicuous page if bleed-through is a concern.",
    cautions: "Page layouts may vary beyond the photographed spread; exact format requires confirmation from the physical item.",
    features: ["Hard cover", "Elastic closure", "Ribbon marker", "Ruled and dotted pages pictured"],
  }),
  makeProduct({
    id: 16,
    name: "Green Ceramic Plant-Watering Reservoir",
    category: "Plant Care",
    price: 128,
    inventory: 13,
    colors: ["Matte green / natural cork"],
    material: "Visible only: a matte green ceramic-looking vessel with a removable cork stopper. Ceramic porosity and cork composition are not certified by the images.",
    specifications: "One small bulb-shaped reservoir is shown alone and partly buried in potting soil beside a houseplant.",
    use: "The planted view indicates a refillable reservoir intended to release water into surrounding potting soil.",
    detail: "The stopper covers the top opening; capacity and release rate are not provided.",
    care: "Insert into loose soil without forcing, rinse between uses and protect from impact and freezing.",
    cautions: "Continue checking soil moisture; watering rate varies with pot, soil, plant and temperature.",
    features: ["Bulb-shaped vessel", "Top refill opening", "Removable cork stopper"],
  }),
  makeProduct({
    id: 17,
    name: "Amber Glass Botanical Candle with Wood Lid",
    category: "Candles — Documentation Pending",
    price: 118,
    inventory: 0,
    purchasable: false,
    colors: ["Amber / cream wax / natural wood"],
    material: "Visible only: an amber glass vessel, pale wax, a single light-colour wick and a wood-look lid. Wax, wick, fragrance and lid materials are not identified.",
    specifications: "One unlabeled single-wick candle is pictured. Net weight, burn time, fragrance name, manufacturer, batch and safety label are not visible.",
    use: "The images show a lidded container candle styled with coriander-like seeds and pink flowers.",
    detail: "The styling props do not establish coriander, geranium, vetiver or any fragrance formula, and the photographs do not prove a 210 g weight or soy wax.",
    care: "Do not burn or sell until the required candle label and verified burn instructions are available.",
    cautions: "CANDLE HOLD: wax composition, fragrance allergens, vessel heat suitability and tested burn behaviour are unknown.",
    features: ["Amber glass vessel", "Single visible wick", "Removable wood-look lid"],
    holdReason: "Unavailable until wax and wick composition, fragrance/allergen disclosure, net weight, batch, manufacturer, tested burn time and fire-safety label are supplied.",
  }),
  makeProduct({
    id: 18,
    name: "Amber Pump-Bottle Care Duo",
    category: "Personal Care — Documentation Pending",
    price: 168,
    inventory: 0,
    purchasable: false,
    size: ["Two bottles (as pictured)"],
    colors: ["Amber / black / sage label panels"],
    material: "Visible only: two amber-colour pump bottles, one containing clear gel-like liquid and one opaque cream-like liquid. Formulas and bottle materials are not shown.",
    specifications: "Two unlabeled pump bottles are pictured. Product identity, volume, ingredients, batch, expiry/PAO and manufacturer are not visible.",
    use: "The pair appears styled as complementary personal-care products, but hand wash, lotion or any other function cannot be confirmed from appearance.",
    detail: "The white cream shown in a dish does not establish the contents or performance of either bottle.",
    care: "Do not apply or distribute until each bottle has a complete, traceable label and safe-use directions.",
    cautions: "COSMETIC-CONTENTS HOLD: clear and opaque liquids cannot be identified by appearance.",
    features: ["Two pump bottles", "Amber-colour containers", "Blank sage label panels"],
    holdReason: "Unavailable until both products have identities, ingredients, allergens, net contents, batch, expiry/PAO, manufacturer and directions.",
  }),
  makeProduct({
    id: 19,
    name: "Gold-Tone Heart Tea Infuser",
    category: "Tea Accessories",
    price: 55,
    inventory: 61,
    colors: ["Gold tone"],
    material: "Visible only: gold-tone fine mesh, a hinged frame, short chain and hook. Base metal and food-contact coating are not shown.",
    specifications: "One heart-shaped mesh infuser with a hinged opening and retrieval chain is pictured.",
    use: "The open view shows loose tea held inside the mesh heart for steeping in a cup or pot.",
    detail: "The fine mesh closes around the tea while the chain remains outside the drink for retrieval.",
    care: "Empty leaves promptly, rinse mesh from both sides and dry with the hinge open.",
    cautions: "Metal becomes hot in freshly boiled water. Verify food-contact coating and keep the small chain away from children.",
    features: ["Heart-shaped mesh basket", "Hinged opening", "Retrieval chain and hook"],
  }),
  makeProduct({
    id: 20,
    name: "Speckled Reusable Cup with Black Lid",
    category: "Drinkware",
    price: 138,
    inventory: 20,
    colors: ["Warm grey speckle / charcoal"],
    material: "Visible only: a rigid speckled cup body and dark press-on-style sip lid. Composition and food-contact grades are not shown.",
    specifications: "One tapered cup with a removable lid is pictured. Capacity, heat rating and closure performance are not provided.",
    use: "A takeaway-style reusable cup for drinks compatible with the instructions on the physical product.",
    detail: "The images do not prove recycled content, insulation or a leakproof seal.",
    care: "Remove the lid for washing and dry both parts fully before storage.",
    cautions: "Carry upright, test the lid fit and confirm temperature suitability before adding a hot drink.",
    features: ["Speckled tapered body", "Removable dark lid", "Sip opening"],
  }),
  makeProduct({
    id: 21,
    name: "Clip-Lid Metal Lunch Box with Divider",
    category: "Food Storage",
    price: 178,
    inventory: 12,
    colors: ["Brushed silver / sage seal"],
    material: "Visible only: a brushed metal box and lid, sage-colour rim seal, metal side clips and removable metal divider. Alloy and seal grades are not shown.",
    specifications: "One rectangular box with two side clips and a single divider is pictured. Capacity, leak resistance and food-contact certification are not visible.",
    use: "The open divided format indicates a lunch or food-storage box for separating two portions.",
    detail: "The divider appears removable and the lid is held by clips on both sides.",
    care: "Remove the divider and seal for cleaning if the physical instructions allow; dry edges and channels before reassembly.",
    cautions: "Do not microwave metal. Carry liquids upright and confirm food-contact and dishwasher guidance on the physical label.",
    features: ["Two side clips", "Sage-colour rim seal", "Removable divider pictured"],
  }),
  makeProduct({
    id: 22,
    name: "Botanical-Print Reusable Food Wrap Set",
    category: "Food Storage",
    price: 86,
    inventory: 34,
    size: ["Three sizes (as pictured)"],
    colors: ["Cream / sage leaves / mustard flowers"],
    material: "Visible only: three flexible botanical-print fabric-like sheets with a coated appearance. Fibre and coating composition are not shown.",
    specifications: "Three graduated square wraps are pictured folded flat and shaped around bread and cut citrus.",
    use: "A reusable wrap set shown covering bread and produce for short-term storage.",
    detail: "The photographs do not establish beeswax, resin, vegan ingredients or exact dimensions.",
    care: "Use only the cleaning method and temperature limits supplied with the physical product; store flat when dry.",
    cautions: "Keep away from heat and verify food-contact, raw-food and allergen guidance before use.",
    features: ["Three graduated sizes", "Botanical print", "Flexible wrap format"],
  }),
  makeProduct({
    id: 23,
    name: "Terracotta Metal Bottle with Screw Cap",
    category: "Water Bottles",
    price: 168,
    inventory: 26,
    colors: ["Matte terracotta / silver rim"],
    material: "Visible only: a metal bottle mouth, terracotta-coated body and matching screw cap with a dark internal stopper. Metal grade and coating are not shown.",
    specifications: "One slim curved bottle with a removable screw cap is pictured. Capacity, insulation and leak test are not provided.",
    use: "A reusable bottle form for compatible drinks when used within the physical product's temperature guidance.",
    detail: "The open view shows the metal neck and the cap's internal sealing component.",
    care: "Wash bottle, cap and removable seal as directed and dry uncapped.",
    cautions: "Do not assume insulation, carbonation compatibility or dishwasher safety; test the closure before carrying.",
    features: ["Slim curved profile", "Screw cap", "Metal mouth", "Terracotta coating"],
  }),
  makeProduct({
    id: 24,
    name: "Wood-Finish Palm Dish Brush with Replaceable Head",
    category: "Cleaning Accessories",
    price: 68,
    inventory: 45,
    colors: ["Natural wood tone / cream"],
    material: "Visible only: a wood-grain handle and round head, pale bristles, internal white connector and light hanging cord. Wood and bristle species are not shown.",
    specifications: "One palm-size brush is pictured assembled and with the round bristle head detached.",
    use: "A compact scrubbing brush for dishes or sink surfaces compatible with its bristle firmness.",
    detail: "The detached view shows a replaceable-style connection between handle and bristle head.",
    care: "Rinse after use, shake out water and dry bristles-down with airflow; do not leave submerged.",
    cautions: "Test on delicate or non-stick finishes and replace the head if bristles deform or the connector loosens.",
    features: ["Palm-shape handle", "Round bristle head", "Detachable head pictured", "Hanging cord"],
  }),
  makeProduct({
    id: 25,
    name: "Natural Mesh Drawstring Produce Bag Trio",
    category: "Shopping Bags",
    price: 75,
    inventory: 33,
    size: ["Three sizes (as pictured)"],
    colors: ["Natural cream"],
    material: "Visible only: three open-mesh fabric bags with matching drawstrings. Fibre composition is not shown.",
    specifications: "Three graduated bags are pictured empty and filled with produce and bread.",
    use: "A reusable drawstring bag set for grouping loose produce or other lightweight shopping items.",
    detail: "The open weave keeps contents visible; exact dimensions and load limits are not provided.",
    care: "Wash according to the physical label, reshape and air-dry before storage.",
    cautions: "Do not overload the seams. Keep drawstrings away from infants and young children.",
    features: ["Three graduated sizes", "Open mesh weave", "Drawstring closures"],
  }),
];

export default productData;
